// web/src/lib/suggestCapabilities.ts
// Deterministic ranking of luna_capabilities entries against a user profile.
//
// No learning, no ML — just a rule set keyed off profile attributes that
// surfaces the 4-5 most likely-relevant analyses at the top of the
// command palette. Falls back to a sensible default order when no profile
// is loaded.
function toNum(v) {
    if (v === undefined || v === null)
        return 0;
    if (typeof v === "number")
        return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}
function hasAccountType(profile, types) {
    const accts = profile?.financial_position?.accounts ?? [];
    return accts.some((a) => a.type !== undefined && types.includes(a.type));
}
function totalBalanceOfType(profile, types) {
    const accts = profile?.financial_position?.accounts ?? [];
    return accts
        .filter((a) => a.type !== undefined && types.includes(a.type))
        .reduce((s, a) => s + toNum(a.balance), 0);
}
function hasConcentratedHolding(profile) {
    // Heuristic: any single holding > 25% of total invested.
    const accts = profile?.financial_position?.accounts ?? [];
    let totalInvested = 0;
    let largestHolding = 0;
    for (const a of accts) {
        for (const h of a.holdings ?? []) {
            const mv = toNum(h.market_value);
            totalInvested += mv;
            if (mv > largestHolding)
                largestHolding = mv;
        }
    }
    return totalInvested > 0 && largestHolding / totalInvested > 0.25;
}
/**
 * Return up to `max` capability ids ranked by relevance to the profile.
 * Capabilities that don't match any rule are not returned (the rest of the
 * palette covers them).
 */
export function suggestCapabilities(capabilities, profile, max = 5) {
    const score = new Map();
    const bump = (id, n) => {
        score.set(id, (score.get(id) ?? 0) + n);
    };
    const age = profile?.personal_family?.age ?? 0;
    const numKids = profile?.personal_family?.num_children ?? 0;
    const married = profile?.personal_family?.marital_status === "married";
    const selfEmployed = profile?.personal_family?.employment_type === "self_employed";
    const pretax = totalBalanceOfType(profile, [
        "401k", "403b", "457b", "tsp", "traditional_ira", "rollover_ira", "sep_ira", "simple_ira",
    ]);
    const hasHsa = hasAccountType(profile, ["hsa_invested", "hsa_cash"]);
    const has529 = hasAccountType(profile, ["529"]);
    const hasTaxable = hasAccountType(profile, ["brokerage"]);
    const hasPension = hasAccountType(profile, ["pension"]);
    const hasBusiness = hasAccountType(profile, ["business_equity"]);
    // --- Headline rules ----------------------------------------------------
    // Pre-retiree with significant pre-tax balance → Roth ladder
    if (age >= 45 && age <= 65 && pretax >= 500_000) {
        bump("taxes-roth-conversion-ladder", 100);
    }
    // Has taxable + retirement accounts → asset location
    if (hasTaxable && pretax > 50_000) {
        bump("taxes-asset-location", 80);
    }
    // Concentrated single position → diversification / stress test
    if (hasConcentratedHolding(profile)) {
        bump("portfolio-stress-2008", 70);
        bump("portfolio-factor", 50);
    }
    // Approaching retirement → withdrawal sequencing
    if (age >= 55) {
        bump("retirement-withdrawal-sequencing", 90);
    }
    // 529 present → education planning
    if (has529) {
        bump("portfolio-allocation", 30);
    }
    // Pension present → pension vs lump sum
    if (hasPension) {
        bump("retirement-pension-vs-lump-sum", 80);
    }
    // Self-employed / business owner → QBI, SEP, solo-401k topics
    if (selfEmployed || hasBusiness) {
        bump("retirement-self-employed", 60);
        bump("taxes-bracket-analysis", 50);
    }
    // Married with kids → life insurance adequacy
    if (married && numKids > 0) {
        bump("insurance-life", 60);
    }
    // HSA holders → triple-tax-advantage / HSA optimization
    if (hasHsa) {
        bump("taxes-asset-location", 20);
    }
    // Younger w/ moderate balance → portfolio fundamentals
    if (age < 45 && pretax < 500_000) {
        bump("portfolio-allocation", 50);
        bump("portfolio-monte-carlo", 40);
    }
    // Universal default boosts so we always have *something* if profile is empty
    bump("portfolio-allocation", 5);
    bump("portfolio-monte-carlo", 4);
    bump("retirement-monte-carlo", 3);
    bump("taxes-roth-conversion-ladder", 2);
    // --- Pick top N ---------------------------------------------------------
    const ranked = [...score.entries()]
        .sort(([, a], [, b]) => b - a)
        .map(([id]) => capabilities.find((c) => c.id === id))
        .filter((c) => c !== undefined);
    return ranked.slice(0, max);
}
